import type { ConsultationVitals, ConsultationVitalsInput } from "@/core/clinical/types";
import { emptyConsultationVitals, formatBloodPressure, hasAnyVitalValue } from "@/core/clinical/types";

export interface VitalsFormState {
  bloodPressureSystolic: string;
  bloodPressureDiastolic: string;
  heartRate: string;
  temperatureC: string;
  respiratoryRate: string;
  oxygenSaturation: string;
  weightKg: string;
  heightCm: string;
  painScore: string;
  notes: string;
}

export function emptyVitalsFormState(): VitalsFormState {
  return {
    bloodPressureSystolic: "",
    bloodPressureDiastolic: "",
    heartRate: "",
    temperatureC: "",
    respiratoryRate: "",
    oxygenSaturation: "",
    weightKg: "",
    heightCm: "",
    painScore: "",
    notes: "",
  };
}

function vitalToField(value: number | null | undefined): string {
  return value != null ? String(value) : "";
}

export function vitalsToFormState(vitals: ConsultationVitals | null | undefined): VitalsFormState {
  if (!vitals) return emptyVitalsFormState();
  return {
    bloodPressureSystolic: vitalToField(vitals.bloodPressureSystolic),
    bloodPressureDiastolic: vitalToField(vitals.bloodPressureDiastolic),
    heartRate: vitalToField(vitals.heartRate),
    temperatureC: vitalToField(vitals.temperatureC),
    respiratoryRate: vitalToField(vitals.respiratoryRate),
    oxygenSaturation: vitalToField(vitals.oxygenSaturation),
    weightKg: vitalToField(vitals.weightKg),
    heightCm: vitalToField(vitals.heightCm),
    painScore: vitalToField(vitals.painScore),
    notes: vitals.notes,
  };
}

export function formStateToVitalsInput(
  form: VitalsFormState,
  previous: ConsultationVitals | null,
): ConsultationVitalsInput {
  return {
    bloodPressureSystolic: form.bloodPressureSystolic,
    bloodPressureDiastolic: form.bloodPressureDiastolic,
    heartRate: form.heartRate,
    temperatureC: form.temperatureC,
    respiratoryRate: form.respiratoryRate,
    oxygenSaturation: form.oxygenSaturation,
    weightKg: form.weightKg,
    heightCm: form.heightCm,
    painScore: form.painScore,
    notes: form.notes,
    recordedAt: previous?.recordedAt ?? null,
  };
}

interface ConsultationVitalsSectionProps {
  form: VitalsFormState;
  vitals: ConsultationVitals | null;
  canEdit: boolean;
  idPrefix?: string;
  onChange: (patch: Partial<VitalsFormState>) => void;
}

function VitalReadItem({ label, value }: { label: string; value: string }) {
  return (
    <>
      <dt className="col-5 col-sm-4 text-muted">{label}</dt>
      <dd className="col-7 col-sm-8 fw-medium">{value}</dd>
    </>
  );
}

export function ConsultationVitalsSection({
  form,
  vitals,
  canEdit,
  idPrefix = "vitals",
  onChange,
}: ConsultationVitalsSectionProps) {
  const recordedLabel =
    vitals?.recordedAt &&
    new Date(vitals.recordedAt).toLocaleString("fr-FR", {
      day: "numeric",
      month: "short",
      hour: "2-digit",
      minute: "2-digit",
    });

  if (!canEdit) {
    if (!hasAnyVitalValue(vitals)) {
      return <p className="text-muted small mb-0">Aucune constante enregistrée.</p>;
    }

    const bp = formatBloodPressure(vitals);
    return (
      <dl className="row mb-0 small g-2">
        {bp && <VitalReadItem label="Tension" value={bp} />}
        {vitals!.heartRate != null && (
          <VitalReadItem label="Pouls" value={`${vitals!.heartRate} bpm`} />
        )}
        {vitals!.temperatureC != null && (
          <VitalReadItem label="Température" value={`${vitals!.temperatureC} °C`} />
        )}
        {vitals!.respiratoryRate != null && (
          <VitalReadItem label="FR" value={`${vitals!.respiratoryRate} /min`} />
        )}
        {vitals!.oxygenSaturation != null && (
          <VitalReadItem label="SpO₂" value={`${vitals!.oxygenSaturation} %`} />
        )}
        {vitals!.weightKg != null && (
          <VitalReadItem label="Poids" value={`${vitals!.weightKg} kg`} />
        )}
        {vitals!.heightCm != null && (
          <VitalReadItem label="Taille" value={`${vitals!.heightCm} cm`} />
        )}
        {vitals!.painScore != null && (
          <VitalReadItem label="Douleur (EVA)" value={`${vitals!.painScore} /10`} />
        )}
        {vitals!.notes && (
          <>
            <dt className="col-12 text-muted">Notes</dt>
            <dd className="col-12 mb-0">{vitals!.notes}</dd>
          </>
        )}
        {recordedLabel && (
          <dt className="col-12 text-muted small mt-1">Relevé le {recordedLabel}</dt>
        )}
      </dl>
    );
  }

  const field = (key: keyof VitalsFormState, label: string, unit: string, opts?: { min?: number; max?: number; step?: string; col?: string }) => (
    <div className={opts?.col ?? "col-6 col-md-4 col-lg-3"}>
      <label className="form-label small fw-semibold mb-1" htmlFor={`${idPrefix}-${key}`}>
        {label}
      </label>
      <div className="input-group input-group-sm">
        <input
          id={`${idPrefix}-${key}`}
          type="number"
          className="form-control"
          min={opts?.min}
          max={opts?.max}
          step={opts?.step ?? "any"}
          value={form[key]}
          placeholder="—"
          onChange={(e) => onChange({ [key]: e.target.value } as Partial<VitalsFormState>)}
        />
        {unit && <span className="input-group-text">{unit}</span>}
      </div>
    </div>
  );

  return (
    <div>
      <div className="row g-2">
        {field("bloodPressureSystolic", "TA systolique", "mmHg", { min: 50, max: 280 })}
        {field("bloodPressureDiastolic", "TA diastolique", "mmHg", { min: 30, max: 180 })}
        {field("heartRate", "Pouls", "bpm", { min: 20, max: 250 })}
        {field("temperatureC", "Température", "°C", { min: 30, max: 45, step: "0.1" })}
        {field("respiratoryRate", "FR", "/min", { min: 4, max: 60 })}
        {field("oxygenSaturation", "SpO₂", "%", { min: 50, max: 100 })}
        {field("weightKg", "Poids", "kg", { min: 1, max: 300, step: "0.1" })}
        {field("heightCm", "Taille", "cm", { min: 30, max: 250 })}
        {field("painScore", "Douleur (EVA)", "/10", { min: 0, max: 10 })}
        <div className="col-12">
          <label className="form-label small fw-semibold mb-1" htmlFor={`${idPrefix}-notes`}>
            Notes constantes
          </label>
          <textarea
            id={`${idPrefix}-notes`}
            className="form-control form-control-sm"
            rows={2}
            placeholder="Position patient, contexte du relevé…"
            value={form.notes}
            onChange={(e) => onChange({ notes: e.target.value })}
          />
        </div>
      </div>
      {recordedLabel && (
        <p className="form-text mb-0 mt-2">Dernier relevé : {recordedLabel}</p>
      )}
    </div>
  );
}

export function vitalsPreviewFromForm(form: VitalsFormState): ConsultationVitals {
  const base = emptyConsultationVitals();
  const num = (s: string) => (s.trim() === "" ? null : Number(s));
  return {
    ...base,
    bloodPressureSystolic: num(form.bloodPressureSystolic),
    bloodPressureDiastolic: num(form.bloodPressureDiastolic),
    heartRate: num(form.heartRate),
    temperatureC: num(form.temperatureC),
    respiratoryRate: num(form.respiratoryRate),
    oxygenSaturation: num(form.oxygenSaturation),
    weightKg: num(form.weightKg),
    heightCm: num(form.heightCm),
    painScore: num(form.painScore),
    notes: form.notes.trim(),
  };
}
